import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bell, BellOff, Check, FileText, Crosshair, Shield, Radio, AlertTriangle, User, Zap } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import setThemeColor from '../lib/themeColor'

// In-app notification feed. Two sections — Unread (sorted newest
// first) and Read (collapsed by default). Tap a notification to
// mark read + optionally navigate via metadata.frago_id /
// metadata.objective_id. Dismiss-all clears the feed.
//
// Recipients only see their own rows (RLS); inserts come from
// server-side trigger functions, never from this page.

const S = {
  page: { minHeight: '100vh', background: '#0e1116', color: '#eaf2fb', fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif" },
  display: { fontFamily: "'Orbitron', 'Rajdhani', sans-serif", letterSpacing: '0.14em', textTransform: 'uppercase' },
  muted: { color: '#9aa6b0' },
  faded: { color: '#5a6470' },
  panel: { background: 'rgba(28, 38, 38, .6)', border: '1px solid rgba(140, 180, 130, .2)' },
  panelUnread: { background: 'rgba(154, 219, 110, .08)', border: '1px solid rgba(154, 219, 110, .4)' },
}

const TYPE_ICON = {
  frago_activated:     { Icon: Zap,           color: '#ffcc33' },
  frago_delegated:     { Icon: FileText,      color: '#4dd6c2' },
  frago_completed:     { Icon: Check,         color: '#9adb6e' },
  frago_failed:        { Icon: AlertTriangle, color: '#ff6a3d' },
  frago_aborted:       { Icon: AlertTriangle, color: '#ff7a3d' },
  objective_completed: { Icon: Check,         color: '#9adb6e' },
  bounty_placed:       { Icon: Crosshair,     color: '#ff6a3d' },
  bounty_claimed:      { Icon: Crosshair,     color: '#9adb6e' },
  kill_pending:        { Icon: AlertTriangle, color: '#ff2a2a' },
  intel_discovered:    { Icon: Radio,         color: '#4dd6ff' },
  commander_briefing:  { Icon: Shield,        color: '#c08a2a' },
  opposing_alert:      { Icon: AlertTriangle, color: '#ff7a3d' },
  scenario_role:       { Icon: User,          color: '#c08a2a' },
  game_status:         { Icon: Radio,         color: '#4dd6c2' },
  // ANU-91 — BLIND MAN safety ceasefire and lift. Red for the call so
  // it screams in the feed; teal for the resume so the contrast reads
  // as "all clear".
  blind_man:           { Icon: AlertTriangle, color: '#ff2a2a' },
  blind_man_resume:    { Icon: Check,         color: '#4dd6c2' },
  // ANU-89 — war crime reports and resolutions.
  war_crime:           { Icon: AlertTriangle, color: '#ff6a3d' },
  war_crime_resolution:{ Icon: Check,         color: '#9adb6e' },
  system:              { Icon: Bell,          color: '#8aa090' },
}

const fmtAgo = (iso) => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export default function Notifications() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [notifs, setNotifs] = useState([])
  const [loading, setLoading] = useState(true)
  const [readCollapsed, setReadCollapsed] = useState(true)

  useEffect(() => { setThemeColor('recon') }, [])

  const fetchNotifs = async () => {
    if (!user?.id) return
    setLoading(true)
    const { data } = await supabase
      .from('player_notifications')
      .select('*')
      .eq('recipient_id', user.id)
      .eq('dismissed', false)
      .order('created_at', { ascending: false })
      .limit(200)
    setNotifs(data || [])
    setLoading(false)
  }

  useEffect(() => { fetchNotifs() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id])

  const handleTap = async (n) => {
    if (!n.read) {
      await supabase.from('player_notifications').update({ read: true }).eq('id', n.id)
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))
    }
    // Opposing-faction intel alerts are info-only — title + body and
    // nothing more. They MUST NOT navigate to the FRAGO dossier even
    // if legacy rows still carry a frago_id in metadata. The dossier
    // is classified to the executing faction (see ANU-61 / dossier
    // faction gate).
    if (n.type === 'opposing_alert') return
    const m = n.metadata || {}
    if (m.frago_id)       navigate(`/frago/${m.frago_id}`)
    else if (m.bounty_id) navigate(`/bounty/${m.bounty_id}`)
  }

  const dismissAll = async () => {
    if (!user?.id) return
    if (!window.confirm('Dismiss all notifications?')) return
    await supabase.from('player_notifications')
      .update({ dismissed: true })
      .eq('recipient_id', user.id)
      .eq('dismissed', false)
    setNotifs([])
  }

  const markAllRead = async () => {
    if (!user?.id) return
    await supabase.from('player_notifications')
      .update({ read: true })
      .eq('recipient_id', user.id)
      .eq('read', false)
      .eq('dismissed', false)
    setNotifs(prev => prev.map(n => ({ ...n, read: true })))
  }

  const unread = notifs.filter(n => !n.read)
  const read   = notifs.filter(n => n.read)

  const renderItem = (n) => {
    const def = TYPE_ICON[n.type] || TYPE_ICON.system
    const Icon = def.Icon
    return (
      <button
        key={n.id}
        onClick={() => handleTap(n)}
        className="w-full text-left p-3"
        style={{
          ...(n.read ? S.panel : S.panelUnread),
          marginBottom: 8, cursor: 'pointer', display: 'block',
        }}>
        <div className="flex items-start gap-3">
          <Icon size={16} color={def.color} style={{ marginTop: 2, flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <p style={{ ...S.display, fontSize: 12, color: def.color, fontWeight: 700 }}>
                {n.title}
              </p>
              <span style={{ ...S.faded, fontSize: 10, flexShrink: 0 }}>{fmtAgo(n.created_at)}</span>
            </div>
            {n.body && (
              <p className="text-sm mt-1" style={{ color: n.read ? '#9aa6b0' : '#e0e8de', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                {n.body}
              </p>
            )}
          </div>
          {!n.read && (
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#9adb6e', flexShrink: 0, marginTop: 6,
            }} />
          )}
        </div>
      </button>
    )
  }

  return (
    <div style={S.page}>
      <div style={{
        background: '#1a1d22', borderBottom: '1px solid rgba(140,180,130,.2)',
        padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#9aa6b0', cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          <ArrowLeft size={14} /> Back
        </button>
        <p style={{ ...S.display, fontSize: 14, color: '#e0e8de' }}>
          <Bell size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
          Notifications
        </p>
        <div className="flex gap-2">
          {unread.length > 0 && (
            <button onClick={markAllRead} title="Mark all read"
              style={{ background: 'none', border: '1px solid rgba(154,219,110,.4)', color: '#9adb6e', padding: '4px 8px', fontSize: 10, cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Mark read
            </button>
          )}
          {notifs.length > 0 && (
            <button onClick={dismissAll} title="Dismiss all"
              style={{ background: 'none', border: '1px solid rgba(255,106,61,.4)', color: '#ff6a3d', padding: '4px 8px', fontSize: 10, cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Dismiss
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto" style={{ padding: 16 }}>
        {loading && <p style={{ ...S.muted, textAlign: 'center', padding: 32 }}>Loading…</p>}

        {!loading && notifs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#5a6470' }}>
            <BellOff size={40} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
            <p style={{ ...S.display, fontSize: 12 }}>No notifications</p>
            <p style={{ fontSize: 12, marginTop: 6 }}>Mission updates will land here.</p>
          </div>
        )}

        {!loading && unread.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ ...S.display, fontSize: 11, color: '#9adb6e', marginBottom: 8 }}>
              Unread — {unread.length}
            </p>
            {unread.map(renderItem)}
          </div>
        )}

        {!loading && read.length > 0 && (
          <div>
            <button onClick={() => setReadCollapsed(c => !c)}
              className="w-full flex items-center justify-between"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}>
              <span style={{ ...S.display, fontSize: 11, color: '#5a6470' }}>
                Read — {read.length}
              </span>
              <span style={{ ...S.faded, fontSize: 10 }}>{readCollapsed ? '▼ show' : '▲ hide'}</span>
            </button>
            {!readCollapsed && read.map(renderItem)}
          </div>
        )}
      </div>
    </div>
  )
}
